import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import tradesService from '../services/tradesService';
import ComplianceFindingsTable from './ComplianceFindingsTable';
import EmployeeTradeCheckRulesModal from './EmployeeTradeCheckRulesModal';

const SCAN_POLL_INTERVAL_MS = 3000;
const TERMINAL_SCAN_STATUSES = ['completed', 'failed'];

const STATUS_FILTERS = [
  { value: '', label: 'All findings' },
  { value: 'potential_violation', label: 'Potential violations' },
  { value: 'near_miss', label: 'Near misses' },
  { value: 'needs_timestamp', label: 'Needs timestamp' },
];

const errorText = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

const quarterLabelFor = (isoDate) => {
  if (!isoDate) return '';
  const [year, month] = isoDate.split('-').map(Number);
  if (!year || !month) return '';
  return `Q${Math.floor((month - 1) / 3) + 1} ${year}`;
};

const quarterToDateRange = (quarterValue) => {
  const input = String(quarterValue || '').trim().toUpperCase();
  const match = input.match(/^(?:([0-9]{4})\s*[-_]?\s*Q([1-4])|Q([1-4])\s*[-_]?\s*([0-9]{4}))$/);
  if (!match) return null;

  const year = Number(match[1] || match[4]);
  const quarter = Number(match[2] || match[3]);
  const startMonth = (quarter - 1) * 3;
  const start = new Date(Date.UTC(year, startMonth, 1));
  const end = new Date(Date.UTC(year, startMonth + 3, 0));

  const toIsoDate = (dt) => dt.toISOString().slice(0, 10);
  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(end),
  };
};

const defaultPeriod = () => {
  const now = new Date();
  const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3 - 3;
  const start = new Date(now.getFullYear(), quarterStartMonth, 1);
  const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
  const iso = (value) => value.toISOString().slice(0, 10);
  return { startDate: iso(start), endDate: iso(end) };
};

/**
 * Admin-only panel that automates the quarterly Employee Trade Check: it
 * compares every employee personal trade against the firm's trades placed for
 * clients over any date range, surfaces potential Code of Ethics violations and
 * near misses, and produces the signed potential violations report.
 */
export default function EmployeeTradeCheckPanel() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const initialPeriod = defaultPeriod();
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [startDate, setStartDate] = useState(initialPeriod.startDate);
  const [endDate, setEndDate] = useState(initialPeriod.endDate);
  const [label, setLabel] = useState(quarterLabelFor(initialPeriod.startDate));
  const [nearMissWindowDays, setNearMissWindowDays] = useState(1);

  const availableQuarters = useMemo(() => {
    const now = new Date();
    const unique = new Set();
    // Current quarter plus the past 8 quarters (9 entries total).
    for (let i = 0; i < 9; i += 1) {
      const quarterDate = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
      const year = quarterDate.getFullYear();
      const quarter = Math.floor(quarterDate.getMonth() / 3) + 1;
      unique.add(`Q${quarter} ${year}`);
    }
    return Array.from(unique);
  }, []);

  const handleQuarterSelection = useCallback((value) => {
    setSelectedQuarter(value);
    const range = quarterToDateRange(value);
    if (!range) return;

    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setLabel(quarterLabelFor(range.startDate));
  }, []);

  const [scans, setScans] = useState([]);
  const [selectedScanId, setSelectedScanId] = useState('');
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [workflowMode, setWorkflowMode] = useState('run');

  const [busy, setBusy] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deletingScanId, setDeletingScanId] = useState('');
  const [busyFindingId, setBusyFindingId] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [reportUrl, setReportUrl] = useState('');

  const pollTimerRef = useRef(null);

  const loadScans = useCallback(async () => {
    try {
      const result = await tradesService.getComplianceScans(25);
      setScans(result.scans || []);
      return result.scans || [];
    } catch (err) {
      console.error('Failed to load compliance scans:', err);
      setError(errorText(err, 'Failed to load previous trade checks.'));
      return [];
    }
  }, []);

  const loadDetail = useCallback(async (scanId, filterValue, updateDetail = true) => {
    if (!scanId) return null;
    try {
      const result = await tradesService.getComplianceScan(scanId, filterValue || null);
      if (updateDetail) {
        setDetail(result);
      }

      // Keep a stable download action available: load any previously
      // generated report link whenever a completed scan is selected/refreshed.
      // Only probe when a report key exists to avoid expected 404 noise.
      if (result?.scan?.status === 'completed' && result?.scan?.report_file_key) {
        try {
          const report = await tradesService.getComplianceReport(scanId);
          setReportUrl(report?.download_url || '');
        } catch (reportErr) {
          if (reportErr?.response?.status === 404) {
            setReportUrl('');
          } else {
            console.error('Failed to load existing violations report:', reportErr);
          }
        }
      } else {
        setReportUrl('');
      }

      return result;
    } catch (err) {
      console.error('Failed to load compliance scan detail:', err);
      setError(errorText(err, 'Failed to load the trade check results.'));
      return null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    if (!statusMessage) return undefined;

    const dismissTimer = setTimeout(() => setStatusMessage(''), 10000);
    return () => clearTimeout(dismissTimer);
  }, [statusMessage]);

  const startPolling = useCallback((scanId, hideDetailsUntilComplete = false) => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      const result = await loadDetail(
        scanId,
        statusFilter,
        !hideDetailsUntilComplete,
      );
      const scanStatus = result?.scan?.status;
      if (scanStatus && TERMINAL_SCAN_STATUSES.includes(scanStatus)) {
        if (hideDetailsUntilComplete) {
          setDetail(result);
        }
        setDetailLoading(false);
        stopPolling();
        setBusy(false);
        if (scanStatus === 'failed') {
          setError(result.scan.error || 'The trade check failed.');
          setStatusMessage('');
        } else {
          setStatusMessage(result.scan.progress_message || 'Trade check complete.');
        }
        await loadScans();
      } else if (!result) {
        setDetailLoading(false);
      }
    }, SCAN_POLL_INTERVAL_MS);
  }, [loadDetail, loadScans, statusFilter, stopPolling]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const loaded = await loadScans();
      if (cancelled || !loaded.length) return;
      const latest = loaded[0];
      setSelectedScanId(latest.scan_id);
      setDetailLoading(true);
      const result = await loadDetail(latest.scan_id, '');
      if (!cancelled) {
        setDetailLoading(false);
      }
      if (!cancelled && result && !TERMINAL_SCAN_STATUSES.includes(result.scan.status)) {
        setBusy(true);
        startPolling(latest.scan_id);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
    // Bootstrap should run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRunScan = async () => {
    setWorkflowMode('run');
    stopPolling();
    setDetail(null);
    setSelectedScanId('');
    setDetailLoading(true);
    setError('');
    setStatusMessage('');
    setReportUrl('');
    setBusy(true);
    try {
      const result = await tradesService.startComplianceScan({
        startDate,
        endDate,
        label,
        nearMissWindowDays,
      });
      const scanId = result?.scan?.scan_id;
      if (!scanId) {
        throw new Error('The trade check response did not include a scan id.');
      }
      setSelectedScanId(scanId);
      setStatusMessage('Trade check queued.');
      await loadScans();
      startPolling(scanId, true);
    } catch (err) {
      console.error('Failed to start compliance scan:', err);
      setError(errorText(err, 'Failed to start the trade check.'));
      setBusy(false);
      setDetailLoading(false);
    }
  };

  const handleShowRunForm = () => {
    stopPolling();
    setWorkflowMode('run');
    setDetail(null);
    setSelectedScanId('');
    setDetailLoading(false);
    setReportUrl('');
    setError('');
    setBusy(false);
  };

  const handleShowPreviousChecks = async () => {
    stopPolling();
    setWorkflowMode('view');
    setDetail(null);
    setReportUrl('');
    setError('');
    setDetailLoading(true);

    const scanId = selectedScanId || scans[0]?.scan_id;
    if (!scanId) {
      setDetailLoading(false);
      return;
    }

    setSelectedScanId(scanId);
    const result = await loadDetail(scanId, statusFilter);
    setDetailLoading(false);
    if (result && !TERMINAL_SCAN_STATUSES.includes(result.scan.status)) {
      setBusy(true);
      startPolling(scanId);
    } else {
      setBusy(false);
    }
  };

  const handleSelectScan = async (scanId) => {
    setWorkflowMode('view');
    stopPolling();
    setDetail(null);
    setSelectedScanId(scanId);
    setReportUrl('');
    setError('');
    setDetailLoading(true);
    const result = await loadDetail(scanId, statusFilter);
    setDetailLoading(false);
    if (result && !TERMINAL_SCAN_STATUSES.includes(result.scan.status)) {
      setBusy(true);
      startPolling(scanId);
    } else {
      setBusy(false);
    }
  };

  const handleFilterChange = async (value) => {
    setStatusFilter(value);
    await loadDetail(selectedScanId, value);
  };

  const handleDeleteScan = async () => {
    if (!scan || deletingScanId || !TERMINAL_SCAN_STATUSES.includes(scan.status)) return;

    const confirmation = window.confirm(
      `Delete trade check "${scan.label || `${scan.start_date} to ${scan.end_date}`}"?\n\n`
      + 'This permanently removes its findings and generated violations report.',
    );
    if (!confirmation) return;

    setError('');
    setStatusMessage('Deleting trade check...');
    setDetail(null);
    setDetailLoading(true);
    setDeletingScanId(scan.scan_id);
    try {
      await tradesService.deleteComplianceScan(scan.scan_id);
      const remainingScans = await loadScans();
      const nextScan = remainingScans[0];
      setStatusFilter('');
      setReportUrl('');
      if (nextScan) {
        setSelectedScanId(nextScan.scan_id);
        await loadDetail(nextScan.scan_id, '');
      } else {
        setSelectedScanId('');
        setDetail(null);
        setWorkflowMode('run');
      }
      setStatusMessage('Trade check deleted.');
    } catch (err) {
      console.error('Failed to delete trade check:', err);
      setError(errorText(err, 'Failed to delete the trade check.'));
      setStatusMessage('');
    } finally {
      setDetailLoading(false);
      setDeletingScanId('');
    }
  };

  const handleDispositionChange = async (finding, disposition) => {
    setBusyFindingId(finding.finding_id);
    setError('');
    try {
      await tradesService.updateComplianceFinding(selectedScanId, finding.finding_id, {
        disposition,
      });
      await loadDetail(selectedScanId, statusFilter);
      await loadScans();
    } catch (err) {
      console.error('Failed to update finding:', err);
      setError(errorText(err, 'Failed to save the review decision.'));
    } finally {
      setBusyFindingId('');
    }
  };

  const handleGenerateReport = async () => {
    setReportBusy(true);
    setError('');
    setStatusMessage('Building violations report...');
    try {
      const result = await tradesService.generateComplianceReport(selectedScanId);
      setReportUrl(result.download_url);
      setStatusMessage(
        `Report generated with ${result.included_finding_count} finding${
          result.included_finding_count === 1 ? '' : 's'
        }.`,
      );
      await loadScans();
      return result;
    } catch (err) {
      console.error('Failed to generate violations report:', err);
      setError(errorText(err, 'Failed to generate the violations report.'));
      return null;
    } finally {
      setReportBusy(false);
    }
  };

  const handleBottomReportClick = async () => {
    if (!scan || scan.status !== 'completed' || reportBusy) return;

    if (!reviewComplete) {
      setError(
        `Finish reviewing all findings before generating the report. ${pendingReviewCount} pending.`,
      );
      return;
    }

    if (confirmedViolationCount <= 0) {
      setError('No confirmed violations are available to include in the report.');
      return;
    }

    if (reportUrl) {
      window.open(reportUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const result = await handleGenerateReport();
    if (result?.download_url) {
      window.open(result.download_url, '_blank', 'noopener,noreferrer');
    }
  };

  const scan = detail?.scan;
  const summary = scan?.summary || {};
  const findings = detail?.findings || [];
  const totalFindings = Number(summary.finding_count || 0);
  const confirmedViolationCount = Number(summary.confirmed_violation_count || 0);
  const clearedCount = Number(summary.cleared_count || 0);
  const pendingReviewCount = Math.max(totalFindings - confirmedViolationCount - clearedCount, 0);
  const reviewComplete = pendingReviewCount === 0;
  const canReport = scan?.status === 'completed' && reviewComplete && confirmedViolationCount > 0;

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <button
          type="button"
          className="btn btn-sm btn-link text-decoration-none text-body p-0 d-inline-flex align-items-center"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-controls="employee-trade-check-body"
        >
          <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`} aria-hidden="true"></i>
          <span className="fw-semibold">Employee Trade Check</span>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setShowRulesModal(true)}
        >
          <i className="bi bi-question-circle me-1" aria-hidden="true"></i>
          Hit / Near miss rules
        </button>
      </div>
      <div
        id="employee-trade-check-body"
        className={isExpanded ? 'card-body' : 'card-body d-none'}
      >
        <p className="text-muted small">
          Compares employee personal trades against trades the firm placed for clients over
          any period. Same-day trades that share a long or short bias — for example an
          employee buy before a client buy, or a covered-call sale before a client sell —
          are flagged as hits. The same directional pairing inside the near-miss window,
          but not on the same trading day, is reported as a near miss; the window counts
          trading sessions, skipping weekends and market holidays. Trades in an employee's
          own client account are ignored using a fuzzy account-number and broker match.
        </p>

        <div className="row g-2 align-items-end">
          <div className="col-lg-3">
            <div className="btn-group btn-group-sm w-100" role="group" aria-label="Trade check workflow">
                <button
                  type="button"
                  className={`btn ${workflowMode === 'run' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={handleShowRunForm}
                >
                  <i className="bi bi-play-circle me-1" aria-hidden="true"></i>
                  Run new check
                </button>
                <button
                  type="button"
                  className={`btn ${workflowMode === 'view' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={handleShowPreviousChecks}
                  disabled={!scans.length}
                >
                  <i className="bi bi-clock-history me-1" aria-hidden="true"></i>
                  View previous check
                </button>
            </div>
          </div>
          {workflowMode === 'run' && (
            <>
            <div className="col">
            <label className="form-label small mb-1" htmlFor="etc-quarter-preset">Quarter Preset</label>
            <select
              id="etc-quarter-preset"
              className="form-select form-select-sm"
              value={selectedQuarter}
              onChange={(event) => handleQuarterSelection(event.target.value)}
            >
              <option value="">Custom range</option>
              {availableQuarters.map((quarter) => (
                <option key={quarter} value={quarter}>{quarter}</option>
              ))}
            </select>
          </div>
          <div className="col">
            <label className="form-label small mb-1" htmlFor="etc-start-date">Start date</label>
            <input
              id="etc-start-date"
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              onChange={(event) => {
                setSelectedQuarter('');
                setStartDate(event.target.value);
                setLabel(quarterLabelFor(event.target.value));
              }}
            />
          </div>
          <div className="col">
            <label className="form-label small mb-1" htmlFor="etc-end-date">End date</label>
            <input
              id="etc-end-date"
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              onChange={(event) => {
                setSelectedQuarter('');
                setEndDate(event.target.value);
              }}
            />
          </div>
          <div className="col">
            <label className="form-label small mb-1" htmlFor="etc-label">Report label</label>
            <input
              id="etc-label"
              type="text"
              className="form-control form-control-sm"
              placeholder="Q2 2026"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </div>
          <div className="col">
            <label className="form-label small mb-1" htmlFor="etc-window">
              Near-miss window
            </label>
            <select
              id="etc-window"
              className="form-select form-select-sm"
              value={nearMissWindowDays}
              onChange={(event) => setNearMissWindowDays(Number(event.target.value))}
            >
              <option value={1}>± 1 trading day</option>
              <option value={2}>± 2 trading days</option>
              <option value={3}>± 3 trading days</option>
            </select>
          </div>
            <div className="col d-grid">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleRunScan}
              disabled={busy || !startDate || !endDate}
            >
              {busy ? 'Running...' : 'Run trade check'}
            </button>
          </div>
            </>
          )}
          {workflowMode === 'view' && scans.length > 0 && (
            <div className="col">
              <label className="form-label small mb-1" htmlFor="etc-scan">Previous checks</label>
              <select
                id="etc-scan"
                className="form-select form-select-sm"
                value={selectedScanId}
                onChange={(event) => handleSelectScan(event.target.value)}
              >
                {scans.map((item) => (
                  <option key={item.scan_id} value={item.scan_id}>
                    {item.label || `${item.start_date} to ${item.end_date}`}
                    {' · '}
                    {item.start_date} to {item.end_date}
                    {' · '}
                    {item.status}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        {statusMessage && !error && (
          <div
            className="alert alert-info position-fixed top-0 end-0 m-3"
            style={{ zIndex: 1080, maxWidth: 'min(28rem, calc(100vw - 2rem))' }}
            role="status"
            aria-live="polite"
          >
            {statusMessage}
          </div>
        )}

        <EmployeeTradeCheckRulesModal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} />

        {detailLoading && (
          <div className="d-flex align-items-center gap-2 text-muted mt-4" role="status">
            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <span>{deletingScanId ? 'Deleting trade check...' : 'Loading trade check...'}</span>
          </div>
        )}

        {scan && (
          <div className="mt-4">
            <div className="row g-2 mt-2">
              <div className="col-6 col-md-2">
                <div className="border rounded p-2">
                  <div className="small text-muted">Potential violations</div>
                  <div className="fs-5 fw-semibold text-danger">
                    {summary.potential_violation_count || 0}
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="border rounded p-2">
                  <div className="small text-muted">Near misses</div>
                  <div className="fs-5 fw-semibold">{summary.near_miss_count || 0}</div>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="border rounded p-2">
                  <div className="small text-muted">Needs timestamp</div>
                  <div className="fs-5 fw-semibold">{summary.needs_timestamp_count || 0}</div>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="border rounded p-2">
                  <div className="small text-muted">Employees reviewed</div>
                  <div className="fs-5 fw-semibold">
                    {(scan.employee_count || 0).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="border rounded p-2">
                  <div className="small text-muted">Trades compared</div>
                  <div className="fs-5 fw-semibold">
                    {(scan.employee_trade_count || 0).toLocaleString()}
                    {' / '}
                    {(scan.company_trade_count || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <ComplianceFindingsTable
                findings={findings}
                onDispositionChange={handleDispositionChange}
                busyFindingId={busyFindingId}
              />
            </div>

            <div className="mt-3 d-flex justify-content-between gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleDeleteScan}
                disabled={!TERMINAL_SCAN_STATUSES.includes(scan.status) || Boolean(deletingScanId)}
              >
                {deletingScanId ? 'Deleting...' : 'Delete check'}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${canReport ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={handleBottomReportClick}
                disabled={!canReport || reportBusy}
              >
                {reportBusy
                  ? 'Building report...'
                  : reportUrl
                    ? 'Download violations report'
                    : 'Generate and download report'}
              </button>
            </div>

            <div className="small text-muted mt-2 text-end">
              {scan?.status !== 'completed'
                ? 'Report becomes available after the scan completes.'
                : !reviewComplete
                  ? `${pendingReviewCount} finding(s) still need review before report generation.`
                  : confirmedViolationCount <= 0
                    ? 'No confirmed violations selected for this report.'
                    : 'Report includes confirmed violations only.'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
